/**
 * Character picker.
 *
 * Each card shows a paper-doll built from the same pixels that texture the 3D
 * model, so what you pick is exactly what you get. The choice is remembered in
 * localStorage - a per-browser convenience, not real save data; world saving
 * lands in phase 5 and the chosen skin should move there with it.
 */

import { PICKABLE_SKINS, paintFrontView, type SkinDef } from "../core/player/skins.js";
import { ui, type Lang } from "../core/text/lang.js";

/**
 * Where the choice is kept.
 *
 * Exported because the home screen picks a character too, before the game
 * exists, and two widgets writing two keys would be two answers to "who am
 * I" - with whichever ran last quietly winning.
 */
export const SKIN_KEY = "vyzhyvannia.skin";
/** Preview upscale. Nearest-neighbour, so the pixels stay crisp. */
const PREVIEW_SCALE = 5;

/**
 * A paper doll of one character, built from the pixels that texture the
 * model - so what you pick is exactly what you get.
 *
 * Exported for the home screen's strip, which draws the same people smaller.
 */
export function previewCanvas(def: SkinDef, scale = PREVIEW_SCALE): HTMLCanvasElement {
  const view = paintFrontView(def);
  const src = document.createElement("canvas");
  src.width = view.w;
  src.height = view.h;
  const sctx = src.getContext("2d");
  if (sctx === null) throw new Error("2D canvas context unavailable");
  const image = sctx.createImageData(view.w, view.h);
  image.data.set(view.px);
  sctx.putImageData(image, 0, 0);

  const out = document.createElement("canvas");
  out.width = view.w * scale;
  out.height = view.h * scale;
  const octx = out.getContext("2d");
  if (octx === null) throw new Error("2D canvas context unavailable");
  octx.imageSmoothingEnabled = false;
  octx.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

/** The skin chosen last time, or the first one. */
export function loadSavedSkin(): SkinDef {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(SKIN_KEY);
  } catch {
    // Private windows and blocked site data both throw here. Not a problem.
  }
  return PICKABLE_SKINS.find((s) => s.id === saved) ?? PICKABLE_SKINS[0]!;
}

export class SkinPicker {
  /** Set by main.ts. */
  lang: Lang = "uk";
  private readonly panel: HTMLDivElement;
  private readonly cards = new Map<string, HTMLElement>();

  private current: SkinDef;
  open = false;

  /** Fired when a different character is chosen. */
  onSelect: ((def: SkinDef) => void) | null = null;
  /** Fired when the panel closes, so the game can take the pointer back. */
  onClose: (() => void) | null = null;

  constructor(root: HTMLElement, initial: SkinDef) {
    this.current = initial;

    this.panel = document.createElement("div");
    this.panel.id = "skin-picker";
    this.panel.hidden = true;

    const title = document.createElement("div");
    title.className = "inv-title";
    title.textContent = ui("skins-title", this.lang);

    const row = document.createElement("div");
    row.className = "skin-row";

    for (const def of PICKABLE_SKINS) {
      const card = document.createElement("button");
      card.className = "skin-card";
      card.type = "button";

      const art = document.createElement("div");
      art.className = "skin-art";
      art.appendChild(previewCanvas(def));

      const name = document.createElement("div");
      name.className = "skin-name";
      name.textContent = this.lang === "en" ? def.en : def.name;

      const blurb = document.createElement("div");
      blurb.className = "skin-blurb";
      blurb.textContent = this.lang === "en" ? def.blurbEn : def.blurb;

      card.append(art, name, blurb);
      card.addEventListener("click", () => this.select(def));
      row.appendChild(card);
      this.cards.set(def.id, card);
    }

    const hint = document.createElement("div");
    hint.className = "inv-hint";
    hint.textContent = ui("skins-hint", this.lang);

    this.panel.append(title, row, hint);
    root.appendChild(this.panel);
    this.markSelected();
  }

  private markSelected(): void {
    for (const [id, card] of this.cards) {
      card.classList.toggle("selected", id === this.current.id);
    }
  }

  select(def: SkinDef): void {
    this.current = def;
    this.markSelected();
    try {
      localStorage.setItem(SKIN_KEY, def.id);
    } catch {
      // Not being able to remember the choice is not worth failing over.
    }
    this.onSelect?.(def);
  }

  get selected(): SkinDef {
    return this.current;
  }

  toggle(): void {
    this.open ? this.close() : this.show();
  }

  show(): void {
    this.open = true;
    this.panel.hidden = false;
  }

  close(): void {
    this.open = false;
    this.panel.hidden = true;
    this.onClose?.();
  }
}
